import { getLang, initI18n } from "./i18n";
import {
  FULL_EDITION_NOTE,
  FREE_EDITION_NOTE,
  pickText,
  PRODUCT_META,
} from "./site-data";
import { productMark, productThumb } from "./product-visual";

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] as string,
  );
}

function renderCards(): void {
  const lang = getLang();
  const isZh = lang === "zh";
  const root = document.getElementById("product-grid");
  if (!root) return;
  root.innerHTML = "";

  PRODUCT_META.forEach((p, index) => {
    const card = document.createElement("article");
    card.className = `product-row ${index % 2 === 1 ? "is-reverse" : ""}`;
    card.dataset.slug = p.slug;

    // 七款软件都是「免费版可用 + 完整版可选」，所以角标只有一种：免费可用。
    // 具体解锁了什么不在网页上列，由软件自己的「解锁全部功能」页说明。
    const statusBadge = `<span class="row-badge free">${isZh ? "免费可用" : "Free to use"}</span>`;

    const instrumentScopeText = pickText(p.instrumentScope, lang);

    // 单产品页已取消：这里给出最常用的入口。
    // 七款共用同一个下载页（/free/download.html）：中文看百度网盘、英文看 Google Drive。
    const actions = `
      <a class="btn" href="/free/download.html">${isZh ? "免费下载" : "Free download"}</a>
      <a class="btn secondary" href="/quickstart/${p.slug}.html">${isZh ? "快速上手" : "Quick Start"}</a>
      <a class="btn secondary" href="/details/${p.slug}.html">${isZh ? "软件界面详细说明" : "Software UI reference"}</a>
      <a class="btn secondary" href="/buy.html">${isZh ? "购买完整版" : "Buy full version"}</a>
    `;

    const extraLinks = `
      <a class="row-link" href="/manuals/blender/${p.slug}.html">${isZh ? "Blender 插件手册" : "Blender plugin guide"}</a>
      <a class="row-link" href="/manuals/unreal/${p.slug}.html">${isZh ? "Unreal 插件手册" : "Unreal plugin guide"}</a>
      <a class="row-link" href="/advanced/${p.slug}.html">${isZh ? "自定义人物教程" : "Custom performer tutorial"}</a>
      <a class="row-link" href="/update.html?p=${p.slug}">${isZh ? "更新日志" : "Release notes"}</a>
    `;

    card.innerHTML = `
      <div class="row-copy">
        <div class="row-head">
          <h3>${productMark(p.key)} <span>${escapeHtml(pickText(p.title, lang))}</span></h3>
          ${statusBadge}
        </div>
        <p class="row-tagline">${escapeHtml(pickText(p.tagline, lang))}</p>
        <p class="row-note">${escapeHtml(instrumentScopeText)}</p>
        <p class="row-note">${escapeHtml(pickText(FREE_EDITION_NOTE, lang))}</p>
        <p class="row-note muted">${escapeHtml(pickText(FULL_EDITION_NOTE, lang))}</p>
        <div class="row-actions">${actions}</div>
        <div class="row-links">${extraLinks}</div>
      </div>

      <div class="row-visual">
        <img src="${productThumb(p.key)}" width="480" height="270" loading="lazy" decoding="async" alt="${escapeHtml(pickText(p.title, lang))} ${isZh ? "预览图" : "preview"}" />
      </div>
    `;
    root.appendChild(card);
  });
}

function renderStaticText(): void {
  const isZh = getLang() === "zh";
  const title = document.getElementById("products-title");
  const sub = document.getElementById("products-sub");
  const footer = document.getElementById("products-footer");
  // 这一句原来在中英两版都写死中文（英文页会露中文），这里补上双语。
  // 里面有站内链接，所以用 innerHTML。
  const hubNote = document.getElementById("products-hub-note");
  if (hubNote)
    hubNote.innerHTML = isZh
      ? '每款乐器都有两类图文：<a href="/quickstart.html">快速上手</a>（最短流程）与 <a href="/details.html">软件界面详细说明</a>（逐个参数）。'
      : 'Each instrument has two write-ups: <a href="/quickstart.html">Quick Start</a> (the shortest path) and <a href="/details.html">Software UI reference</a> (parameter by parameter).';

  // 页头三个标签：原来写死在 HTML 里，英文页会露中文
  const tagFree = document.getElementById("products-tag-free");
  const tagCount = document.getElementById("products-tag-count");
  const tagMore = document.getElementById("products-tag-more");
  if (tagFree)
    tagFree.textContent = isZh ? "七款软件都能免费使用" : "All 7 are free to use";
  if (tagCount)
    tagCount.textContent = isZh ? "已支持 7 种乐器" : "7 instruments supported";
  if (tagMore)
    tagMore.textContent = isZh ? "更多类型开发中" : "More instruments in development";
  const h2 = document.getElementById("products-list-title");
  const h2Sub = document.getElementById("products-list-sub");
  if (title)
    title.textContent = isZh
      ? "已支持 7 种乐器的演奏动画生成"
      : "Performance Animation for 7 Supported Instruments";
  if (sub)
    sub.textContent = isZh
      ? "七款软件都能免费下载使用：内置演奏者直接出片，从 MIDI 到动画的完整流程都能跑。购买完整版可以自定义角色，并开启更多功能。"
      : "All seven are free to download and use: bundled performers work out of the box and the whole MIDI-to-animation pipeline runs. The full version lets you customize performers and open up more features.";
  if (h2)
    h2.textContent = isZh
      ? "七款乐器能力总览"
      : "Capability Overview of 7 Instruments";
  if (h2Sub)
    h2Sub.textContent = isZh
      ? "每个乐器都可独立接入 Blender / Unreal 工作流。"
      : "Each instrument can be integrated independently into Blender/Unreal workflows.";
  if (footer)
    footer.textContent = isZh
      ? "推荐路径：先免费下载一款跑通流程，再按目标乐器扩展制作能力。"
      : "Suggested path: download one for free, run the workflow end to end, then expand to the instruments you need.";
}

function main(): void {
  initI18n();
  renderStaticText();
  renderCards();
  document.addEventListener("langchange", () => {
    renderStaticText();
    renderCards();
  });
}

main();
